import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Search,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Shield,
  Car,
  Download,
  Printer
} from 'lucide-react';
import Navigation from '../components/Navigation';
import Sidebar from '../components/Sidebar';
import Footer from '../components/Footer';
import PdfReferenceNoteModal from '../components/PdfReferenceNoteModal';
import BulkVINInput from '../components/BulkVINInput';
import { useAuth } from '../context/AuthContext';
import {
  searchNvsByVin,
  bulkSearchNvsByVins,
  fetchBulkTheftLienPdfForViewing,
  NvsSearchResponse,
  NvsBulkSearchResponse,
  getErrorMessage,
  generateTheftLienPdfReport,
  fetchTheftLienPdfForViewing,
  fetchNvsPricing
} from '../services/nvsService';
import { validateVINQuick, isVINFormatValid, formatVehicleInfo, getVINErrorMessage, VINDecodeResult } from '../services/vinService';
import { BulkVinItem } from '../services/vehicleReportService';
import { getDashboardSettings } from '../services/settingsService';

// Session storage keys for persisting search results
const NVS_SESSION_KEYS = {
  VIN: 'nvs_search_vin',
  REPORT_DATA: 'nvs_search_report_data',
  VIN_DECODE_RESULT: 'nvs_search_vin_decode_result',
  VIN_VALIDATION_ERROR: 'nvs_search_vin_validation_error',
  MODE: 'nvs_search_mode',
};

// Helper functions for session storage
const loadFromSession = <T,>(key: string): T | null => {
  try {
    const stored = sessionStorage.getItem(key);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

const saveToSession = (key: string, value: unknown): void => {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Session storage might be full or disabled
  }
};

const clearNvsSession = (): void => {
  Object.values(NVS_SESSION_KEYS).forEach((key) => {
    sessionStorage.removeItem(key);
  });
};

const LienTheftRecord: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isLoading } = useAuth();

  // Redirect if user doesn't have Theft/Lien access
  useEffect(() => {
    if (isLoading) return;
    if (user?.canAccessTheftLien === false) {
      navigate('/dashboard');
    }
  }, [user, isLoading, navigate]);

  // Initialize state from sessionStorage if available
  const [vin, setVin] = useState(() => loadFromSession<string>(NVS_SESSION_KEYS.VIN) || '');
  const [isSearching, setIsSearching] = useState(false);
  const [reportData, setReportData] = useState<NvsSearchResponse | null>(() =>
    loadFromSession<NvsSearchResponse>(NVS_SESSION_KEYS.REPORT_DATA)
  );
  const [error, setError] = useState('');
  const [isValidatingVIN, setIsValidatingVIN] = useState(false);
  const [vinDecodeResult, setVinDecodeResult] = useState<VINDecodeResult | null>(() =>
    loadFromSession<VINDecodeResult>(NVS_SESSION_KEYS.VIN_DECODE_RESULT)
  );
  const [vinValidationError, setVinValidationError] = useState<string>(() =>
    loadFromSession<string>(NVS_SESSION_KEYS.VIN_VALIDATION_ERROR) || ''
  );
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [showPricing, setShowPricing] = useState(true);
  const [nvsCostPerVin, setNvsCostPerVin] = useState<number | null>(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [pdfError, setPdfError] = useState<string>('');
  const [showPdfModal, setShowPdfModal] = useState(false);

  // Bulk mode state
  const [mode, setMode] = useState<'single' | 'bulk'>(() =>
    (loadFromSession<string>(NVS_SESSION_KEYS.MODE) as 'single' | 'bulk') || 'single'
  );
  const [bulkResults, setBulkResults] = useState<NvsBulkSearchResponse | null>(null);
  const [isBulkSearching, setIsBulkSearching] = useState(false);
  const [bulkError, setBulkError] = useState('');

  // Bulk PDF inline preview state
  const [bulkPdfPreviewUrl, setBulkPdfPreviewUrl] = useState<string | null>(null);
  const [bulkPdfPreviewLoading, setBulkPdfPreviewLoading] = useState(false);
  const [bulkPdfPreviewError, setBulkPdfPreviewError] = useState('');

  // Iframe refs for print
  const singlePdfIframeRef = useRef<HTMLIFrameElement>(null);
  const bulkPdfIframeRef = useRef<HTMLIFrameElement>(null);

  // PDF inline preview state (single mode)
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState<string | null>(null);
  const [pdfPreviewLoading, setPdfPreviewLoading] = useState(false);
  const [pdfPreviewError, setPdfPreviewError] = useState('');

  // Persist state changes to sessionStorage
  useEffect(() => {
    saveToSession(NVS_SESSION_KEYS.VIN, vin);
  }, [vin]);

  useEffect(() => {
    if (reportData) {
      saveToSession(NVS_SESSION_KEYS.REPORT_DATA, reportData);
    }
  }, [reportData]);

  useEffect(() => {
    if (vinDecodeResult) {
      saveToSession(NVS_SESSION_KEYS.VIN_DECODE_RESULT, vinDecodeResult);
    }
  }, [vinDecodeResult]);

  useEffect(() => {
    saveToSession(NVS_SESSION_KEYS.VIN_VALIDATION_ERROR, vinValidationError);
  }, [vinValidationError]);

  // Fetch dashboard settings
  useEffect(() => {
    const fetchSettings = async () => {
      const settings = await getDashboardSettings();
      setShowPricing(settings.showPricing);
    };
    fetchSettings();
  }, []);

  // Fetch NVS pricing on mount
  useEffect(() => {
    let cancelled = false;
    const loadPricing = async () => {
      try {
        const pricing = await fetchNvsPricing();
        if (!cancelled) setNvsCostPerVin(pricing.costPerVin);
      } catch {
        if (!cancelled) setNvsCostPerVin(null);
      }
    };
    loadPricing();
    return () => { cancelled = true; };
  }, []);

  // Body scroll lock when sidebar is open on mobile
  useEffect(() => {
    if (isSidebarOpen && window.innerWidth < 1024) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isSidebarOpen]);

  // Auto-fetch PDF for inline preview whenever single-mode results arrive.
  React.useEffect(() => {
    if (mode !== 'single' || !reportData) {
      setPdfPreviewUrl((prev) => {
        if (prev) window.URL.revokeObjectURL(prev);
        return null;
      });
      setPdfPreviewError('');
      return;
    }

    let cancelled = false;
    let createdUrl: string | null = null;

    setPdfPreviewLoading(true);
    setPdfPreviewError('');

    fetchTheftLienPdfForViewing(reportData.vin)
      .then((url) => {
        if (cancelled) { window.URL.revokeObjectURL(url); return; }
        createdUrl = url;
        setPdfPreviewUrl((prev) => {
          if (prev) window.URL.revokeObjectURL(prev);
          return url;
        });
      })
      .catch((err) => {
        if (cancelled) return;
        setPdfPreviewError(getErrorMessage(err));
      })
      .finally(() => {
        if (!cancelled) setPdfPreviewLoading(false);
      });

    return () => {
      cancelled = true;
      if (createdUrl) window.URL.revokeObjectURL(createdUrl);
    };
  }, [mode, reportData]);

  // Auto-fetch bulk PDF for inline preview whenever bulk results arrive.
  React.useEffect(() => {
    if (mode !== 'bulk' || !bulkResults) {
      setBulkPdfPreviewUrl((prev) => {
        if (prev) window.URL.revokeObjectURL(prev);
        return null;
      });
      setBulkPdfPreviewError('');
      return;
    }

    const items = bulkResults.results
      .filter((r) => r.success)
      .map((r) => ({ vin: r.vin, referenceNote: r.referenceNote }));

    if (items.length === 0) return;

    let cancelled = false;
    let createdUrl: string | null = null;

    setBulkPdfPreviewLoading(true);
    setBulkPdfPreviewError('');

    fetchBulkTheftLienPdfForViewing({ items })
      .then((url) => {
        if (cancelled) { window.URL.revokeObjectURL(url); return; }
        createdUrl = url;
        setBulkPdfPreviewUrl((prev) => {
          if (prev) window.URL.revokeObjectURL(prev);
          return url;
        });
      })
      .catch((err) => {
        if (cancelled) return;
        setBulkPdfPreviewError(getErrorMessage(err));
      })
      .finally(() => {
        if (!cancelled) setBulkPdfPreviewLoading(false);
      });

    return () => {
      cancelled = true;
      if (createdUrl) window.URL.revokeObjectURL(createdUrl);
    };
  }, [mode, bulkResults]);

  // Redirect if not authenticated (only after loading completes)
  React.useEffect(() => {
    if (!isLoading && !user) {
      navigate('/login');
    }
  }, [user, isLoading, navigate]);

  // Pre-fill VIN from URL query parameter
  useEffect(() => {
    const vinParam = searchParams.get('vin');
    if (vinParam && vinParam.length >= 5) {
      const cleanVin = vinParam.replace(/\s/g, '').toUpperCase();
      setVin(cleanVin);
      handleVINValidation(cleanVin);
    }
  }, [searchParams]);

  // Handle VIN validation - quick validation first for instant preview, then full decode in background
  const handleVINValidation = async (vinValue: string) => {
    // Clear previous validation results
    setVinValidationError('');
    setVinDecodeResult(null);

    // Skip validation if VIN is empty or too short (older VINs can be 5+ characters)
    if (!vinValue || vinValue.length < 5) {
      return;
    }

    // Basic format validation
    if (!isVINFormatValid(vinValue)) {
      setVinValidationError('Invalid VIN format. VIN cannot contain I, O, or Q.');
      return;
    }

    // Step 1: Quick validation for instant preview (local decoder, no API calls)
    setIsValidatingVIN(true);
    try {
      const quickResponse = await validateVINQuick(vinValue);

      // Set decode result if available for instant preview
      if (quickResponse.decodeResult) {
        setVinDecodeResult(quickResponse.decodeResult);
      }

      // Set validation error if VIN has warnings
      if (!quickResponse.isValid && quickResponse.errorCode) {
        setVinValidationError(getVINErrorMessage(quickResponse.errorCode));
        setIsValidatingVIN(false);
        return;
      }

      setIsValidatingVIN(false);

      // Quick validation is sufficient for NVS - no background DataOne call
      // (DataOne calls are billable and not needed for NVS workflow)
    } catch (err) {
      console.error('VIN validation error:', err);
      setVinValidationError('Unable to validate VIN. Please try again.');
      setIsValidatingVIN(false);
    }
  };

  // Handle VIN input change
  const handleVINChange = (value: string) => {
    const upperValue = value.replace(/\s/g, '').toUpperCase();
    setVin(upperValue);

    // Clear validation errors when user starts typing
    if (vinValidationError) {
      setVinValidationError('');
    }

    // Auto-validate when VIN reaches 17 characters (standard length)
    if (upperValue.length === 17) {
      handleVINValidation(upperValue);
    } else if (upperValue.length < 5) {
      // Clear decode result if VIN is too short
      setVinDecodeResult(null);
    }
  };

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!vin || vin.length < 5) {
      setError('Please enter a valid VIN (minimum 5 characters)');
      return;
    }

    setError('');
    setIsSearching(true);
    setReportData(null);

    try {
      const response = await searchNvsByVin(vin);
      setReportData(response);
    } catch (err) {
      const errorMessage = getErrorMessage(err);
      setError(errorMessage);
    } finally {
      setIsSearching(false);
    }
  };

  const handleClear = () => {
    setVin('');
    setReportData(null);
    setError('');
    setVinValidationError('');
    setVinDecodeResult(null);
    setPdfError('');
    clearNvsSession();
  };

  const handleModeChange = (newMode: 'single' | 'bulk') => {
    setMode(newMode);
    saveToSession(NVS_SESSION_KEYS.MODE, newMode);
  };

  const handleBulkSearch = async (items: BulkVinItem[]) => {
    setBulkError('');
    setIsBulkSearching(true);
    setBulkResults(null);
    try {
      const response = await bulkSearchNvsByVins(items.map(i => ({ vin: i.vin, referenceNote: i.referenceNote })));
      setBulkResults(response);
    } catch (err) {
      setBulkError(getErrorMessage(err));
    } finally {
      setIsBulkSearching(false);
    }
  };

  const handleGeneratePdf = async (referenceNote: string) => {
    if (!vin) {
      setPdfError('No VIN available for PDF generation');
      setShowPdfModal(false);
      return;
    }

    setPdfError('');
    setIsGeneratingPdf(true);

    try {
      await generateTheftLienPdfReport({
        vin,
        referenceNote: referenceNote || undefined,
      });
      setShowPdfModal(false);
    } catch (err) {
      const errorMessage = getErrorMessage(err);
      setPdfError(errorMessage);
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#f8fafc] to-[#e2e8f0] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#c41e3a] animate-spin" />
      </div>
    );
  }

  // Don't render if not authenticated (will redirect)
  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-[#f8fafc] to-[#e2e8f0] text-[#0f172a]">
      {/* Subtle Brand Ambient Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-1/4 w-[800px] h-[800px] bg-[radial-gradient(ellipse_at_center,rgba(196,30,58,0.04)_0%,transparent_70%)] rounded-full"></div>
        <div className="absolute bottom-0 left-1/4 w-[600px] h-[600px] bg-[radial-gradient(ellipse_at_center,rgba(196,30,58,0.03)_0%,transparent_70%)] rounded-full"></div>
      </div>

      <Navigation onMenuClick={() => setIsSidebarOpen(true)} />
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

      <main className="relative z-10 lg:ml-[220px] pt-[88px] flex-1">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
          {/* Page Header */}
          <div className="glass-card-strong p-6 rounded-xl mb-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-[#c41e3a] text-xs font-bold uppercase tracking-wider">Theft / Lien Intelligence Report</span>
                </div>
                <h1 className="text-2xl md:text-3xl font-bold text-[#0f172a] mb-1">
                  Theft / Lien Record
                </h1>
                <p className="text-[#64748b] text-sm">Search for vehicle liens, theft records, impounds, exports, recalls, and customs inquiries</p>
              </div>
              {reportData && reportData.reportId && (
                <div className="flex items-center gap-4 glass-card px-4 py-3 rounded-lg flex-shrink-0">
                  <div className="text-right">
                    <div className="text-[10px] text-[#64748b] uppercase tracking-wider whitespace-nowrap">Report ID</div>
                    <div className="font-mono text-sm font-semibold text-[#0f172a] whitespace-nowrap">RPT-{reportData.reportId.substring(0, 6).toUpperCase()}</div>
                  </div>
                  <div className="w-px h-8 bg-black/10"></div>
                  <div className="text-right">
                    <div className="text-[10px] text-[#64748b] uppercase tracking-wider whitespace-nowrap">Generated</div>
                    <div className="text-sm font-medium text-[#0f172a] whitespace-nowrap">{new Date(reportData.fetchedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</div>
                  </div>
                  <div className="w-px h-8 bg-black/10"></div>
                  <div className="flex items-center gap-1.5 text-[#16a34a] whitespace-nowrap">
                    <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
                    <span className="text-sm font-semibold">Verified</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Search Card with Mode Tabs */}
          <div className="glass-card-strong rounded-xl mb-6 overflow-hidden">
            <div className="flex border-b border-black/[0.06]">
              <button
                onClick={() => handleModeChange('single')}
                disabled={isSearching || isBulkSearching}
                className={`flex-1 px-6 py-3 text-sm font-medium transition-all ${
                  mode === 'single'
                    ? 'bg-white/80 text-[#0f172a] border-b-2 border-[#c41e3a]'
                    : 'text-[#64748b] hover:text-[#0f172a] hover:bg-white/40'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                Single VIN
              </button>
              <button
                onClick={() => handleModeChange('bulk')}
                disabled={isSearching || isBulkSearching}
                className={`flex-1 px-6 py-3 text-sm font-medium transition-all ${
                  mode === 'bulk'
                    ? 'bg-white/80 text-[#0f172a] border-b-2 border-[#c41e3a]'
                    : 'text-[#64748b] hover:text-[#0f172a] hover:bg-white/40'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                Bulk VINs
              </button>
            </div>

            {/* Single VIN Search */}
            {mode === 'single' && (
              <div className="p-4">
                <form onSubmit={handleSearch}>
                  <div className="flex items-center gap-3">
                    <div className="flex-1 flex items-center gap-3 bg-white rounded-lg px-4 py-3 border border-black/[0.08]">
                      <Search className="w-5 h-5 text-[#94a3b8]" />
                      <input
                        type="text"
                        value={vin}
                        onChange={(e) => handleVINChange(e.target.value)}
                        placeholder="Enter VIN to search for theft and lien records"
                        disabled={isSearching}
                        className="flex-1 bg-transparent text-[#0f172a] font-mono text-sm tracking-wider placeholder-[#94a3b8] focus:outline-none disabled:opacity-50"
                      />
                      <div>
                        {isSearching || isValidatingVIN ? (
                          <Loader2 className="w-5 h-5 text-[#c41e3a] animate-spin" />
                        ) : vinDecodeResult && !vinValidationError ? (
                          <CheckCircle2 className="w-5 h-5 text-[#16a34a]" />
                        ) : vinValidationError ? (
                          <AlertCircle className="w-5 h-5 text-[#d97706]" />
                        ) : null}
                      </div>
                    </div>
                    {showPricing && nvsCostPerVin !== null && (
                      <span className="badge-success px-3 py-2 rounded-lg text-sm font-semibold whitespace-nowrap">
                        {nvsCostPerVin === 0 ? 'No Charge' : `Est. $${nvsCostPerVin.toFixed(2)}`}
                      </span>
                    )}
                    <button
                      type="submit"
                      disabled={isSearching || !vin}
                      className="btn-primary px-6 py-3 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSearching ? 'Searching...' : 'Search VIN'}
                    </button>
                    {(reportData || vin) && (
                      <button
                        type="button"
                        onClick={handleClear}
                        disabled={isSearching}
                        className="btn-secondary px-5 py-3 disabled:opacity-50"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                </form>
              </div>
            )}

            {/* Bulk VIN Input */}
            {mode === 'bulk' && (
              <div className="p-4">
                <BulkVINInput
                  onSubmit={handleBulkSearch}
                  disabled={isBulkSearching}
                  maxVins={50}
                  costPerVin={showPricing ? nvsCostPerVin : null}
                />
              </div>
            )}
          </div>

          {mode === 'single' && error && (
            <div className="mb-6 p-4 bg-[#dc2626]/10 border border-[#dc2626]/30 rounded-xl text-[#dc2626] text-sm flex items-start gap-3">
              <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <p>{error}</p>
            </div>
          )}

          {mode === 'bulk' && bulkError && (
            <div className="mb-6 p-4 bg-[#dc2626]/10 border border-[#dc2626]/30 rounded-xl text-[#dc2626] text-sm flex items-start gap-3">
              <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <p>{bulkError}</p>
            </div>
          )}

          {/* Bulk Results */}
          {mode === 'bulk' && isBulkSearching && (
            <div className="flex items-center justify-center gap-3 py-12">
              <Loader2 className="w-6 h-6 text-[#c41e3a] animate-spin" />
              <span className="text-[#64748b]">Running bulk theft/lien checks...</span>
            </div>
          )}

          {mode === 'bulk' && bulkResults && !isBulkSearching && (
            <div className="glass-card overflow-hidden rounded-xl">
              {bulkPdfPreviewLoading ? (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="w-8 h-8 text-[#c41e3a] animate-spin mr-3" />
                  <span className="text-[#64748b] text-sm">Generating PDF...</span>
                </div>
              ) : bulkPdfPreviewError ? (
                <div className="flex items-center justify-center py-12 px-4">
                  <AlertCircle className="w-5 h-5 text-[#d97706] mr-2 flex-shrink-0" />
                  <span className="text-[#92400e] text-sm">{bulkPdfPreviewError}</span>
                </div>
              ) : bulkPdfPreviewUrl ? (
                <iframe
                  ref={bulkPdfIframeRef}
                  src={`${bulkPdfPreviewUrl}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
                  title="Bulk Theft/Lien PDF Report"
                  className="w-full border-0 bg-white"
                  style={{ height: 'min(1500px, 90vh)' }}
                />
              ) : null}
            </div>
          )}

          {mode === 'single' && vinValidationError && (
            <div className="mb-6 p-4 bg-[#d97706]/10 border border-[#d97706]/30 rounded-xl text-sm">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5 text-[#d97706]" />
                <div>
                  <p className="font-medium text-[#d97706]">{vinValidationError}</p>
                  <p className="text-xs text-[#92400e] mt-1">
                    You can still search this VIN. The system may return data with exceptions.
                  </p>
                </div>
              </div>
            </div>
          )}

          {mode === 'single' && vinDecodeResult && !vinValidationError && (
            <div className="mb-6 glass-card p-4 rounded-xl">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#c41e3a]/10 flex items-center justify-center">
                  <Car className="w-5 h-5 text-[#c41e3a]" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <p className="font-semibold text-[#0f172a]">{formatVehicleInfo(vinDecodeResult)}</p>
                    <div className="flex items-center gap-3">
                      {vinDecodeResult.checkDigitValid && (
                        <span className="badge-success px-2 py-1 rounded text-xs font-medium">Check Digit Valid</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-4 mt-2 text-sm text-[#64748b]">
                    <span>VIN: <span className="font-mono text-[#c41e3a]">{vin}</span></span>
                    {vinDecodeResult.bodyClass && <span>| {vinDecodeResult.bodyClass}</span>}
                    {vinDecodeResult.driveType && <span>| {vinDecodeResult.driveType}</span>}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Idle State */}
          {mode === 'single' && !isSearching && !reportData && !error && (
            <div className="glass-card p-12 rounded-xl">
              <div className="text-center">
                <Shield className="w-16 h-16 text-[#94a3b8] mx-auto mb-4" />
                <h2 className="text-xl font-semibold text-[#475569] mb-2">No Vehicle Data</h2>
                <p className="text-[#64748b]">Enter a VIN above to search for theft and lien records</p>
              </div>
            </div>
          )}

          {/* Searching State */}
          {mode === 'single' && isSearching && (
            <div className="glass-card p-12 rounded-xl">
              <div className="text-center">
                <Loader2 className="w-16 h-16 text-[#c41e3a] animate-spin mx-auto mb-4" />
                <h2 className="text-xl font-semibold text-[#475569] mb-2">Searching...</h2>
                <p className="text-[#64748b]">Querying theft and lien databases</p>
              </div>
            </div>
          )}

          {/* Results State */}
          {mode === 'single' && !isSearching && reportData && (
            <div className="glass-card overflow-hidden rounded-xl">
              {pdfPreviewLoading ? (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="w-8 h-8 text-[#c41e3a] animate-spin mr-3" />
                  <span className="text-[#64748b] text-sm">Loading PDF preview...</span>
                </div>
              ) : pdfPreviewError ? (
                <div className="flex items-center justify-center py-12 px-4">
                  <AlertCircle className="w-5 h-5 text-[#d97706] mr-2 flex-shrink-0" />
                  <span className="text-[#92400e] text-sm">{pdfPreviewError}</span>
                </div>
              ) : pdfPreviewUrl ? (
                <iframe
                  ref={singlePdfIframeRef}
                  src={`${pdfPreviewUrl}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
                  title="Theft/Lien PDF Report"
                  className="w-full border-0 bg-white"
                  style={{ height: 'min(1500px, 90vh)' }}
                />
              ) : null}
            </div>
          )}

          {/* PDF download error (floating button flow) */}
          {mode === 'single' && pdfError && (
            <div className="mt-4 p-4 bg-[#dc2626]/10 border border-[#dc2626]/30 rounded-xl flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-[#dc2626] flex-shrink-0 mt-0.5" />
              <p className="text-sm text-[#dc2626]">{pdfError}</p>
            </div>
          )}
        </div>
      </main>

      <Footer />

      {/* Floating Print pill — single mode */}
      {mode === 'single' && pdfPreviewUrl && !isSearching && !pdfPreviewLoading && (
        <button
          onClick={() => singlePdfIframeRef.current?.contentWindow?.print()}
          className="fixed bottom-8 right-[220px] px-6 py-4 bg-gradient-to-r from-[#c41e3a] to-[#a01830] hover:from-[#a01830] hover:to-[#8a1428] text-white font-semibold rounded-full shadow-[0_8px_32px_rgba(196,30,58,0.35)] transition-all flex items-center gap-2 z-50 hover:scale-105"
        >
          <Printer className="w-5 h-5" />
          <span>Print PDF</span>
        </button>
      )}

      {/* Floating Print pill — bulk mode */}
      {mode === 'bulk' && bulkPdfPreviewUrl && !isBulkSearching && !bulkPdfPreviewLoading && (
        <button
          onClick={() => bulkPdfIframeRef.current?.contentWindow?.print()}
          className="fixed bottom-8 right-8 px-6 py-4 bg-gradient-to-r from-[#c41e3a] to-[#a01830] hover:from-[#a01830] hover:to-[#8a1428] text-white font-semibold rounded-full shadow-[0_8px_32px_rgba(196,30,58,0.35)] transition-all flex items-center gap-2 z-50 hover:scale-105"
        >
          <Printer className="w-5 h-5" />
          <span>Print PDF</span>
        </button>
      )}

      {/* Floating Generate PDF pill — single mode (download with reference note) */}
      {mode === 'single' && reportData && !isSearching && (
        <button
          onClick={() => setShowPdfModal(true)}
          disabled={isGeneratingPdf}
          className="fixed bottom-8 right-8 px-6 py-4 bg-gradient-to-r from-[#c41e3a] to-[#a01830] hover:from-[#a01830] hover:to-[#8a1428] text-white font-semibold rounded-full shadow-[0_8px_32px_rgba(196,30,58,0.35)] transition-all flex items-center gap-2 disabled:from-[#94a3b8] disabled:to-[#64748b] disabled:shadow-none disabled:cursor-not-allowed z-50 hover:scale-105"
          title="Generate PDF Report"
        >
          {isGeneratingPdf ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              <span>Generating...</span>
            </>
          ) : (
            <>
              <Download className="w-5 h-5" />
              <span>Generate PDF</span>
            </>
          )}
        </button>
      )}

      {/* PDF Reference Note Modal */}
      <PdfReferenceNoteModal
        isOpen={showPdfModal}
        onClose={() => setShowPdfModal(false)}
        onGenerate={handleGeneratePdf}
        isGenerating={isGeneratingPdf}
      />
    </div>
  );
};

export default LienTheftRecord;
